import { CARD_REGISTRY } from 'components/HomeComponent/cards/cardRegistry';
import type { CardId } from 'components/HomeComponent/cards/types';

export const PWA_DASHBOARD_COLS = 2;

/** Dashboard cards hidden in installed PWA / ?pwaNav=1. */
export const PWA_HIDDEN_DASHBOARD_CARDS: readonly CardId[] = [
  'teamAllocation',
  'timeToSubmit',
  'submittedReturnsChart',
  'vatNumberChecker',
] as const;

const PWA_HIDDEN_DASHBOARD_CARD_SET = new Set<CardId>(PWA_HIDDEN_DASHBOARD_CARDS);

export function filterPwaDashboardCards(cards: CardId[]): CardId[] {
  return cards.filter((cardId) => !PWA_HIDDEN_DASHBOARD_CARD_SET.has(cardId));
}

export type PwaDashboardLayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW: number;
  minH: number;
};

/** Summary + capture stat tiles (2-column grid in PWA). */
export function isSmallDashboardCard(cardId: CardId): boolean {
  const config = CARD_REGISTRY[cardId];
  return (config?.defaultWidth ?? 4) <= 2;
}

/** Stack large cards full-width; pair small cards two per row. */
export function generatePwaMobileDashboardLayout(
  cards: CardId[]
): PwaDashboardLayoutItem[] {
  let currentY = 0;
  let currentX = 0;
  let rowMaxH = 0;

  const flushPartialRow = () => {
    if (currentX > 0) {
      currentY += rowMaxH;
      currentX = 0;
      rowMaxH = 0;
    }
  };

  return cards.map((cardId) => {
    const config = CARD_REGISTRY[cardId];
    const h = config?.defaultHeight ?? 2;
    const minH = config?.minHeight ?? 2;

    if (isSmallDashboardCard(cardId)) {
      const layout: PwaDashboardLayoutItem = {
        i: cardId,
        x: currentX,
        y: currentY,
        w: 1,
        h,
        minW: 1,
        minH,
      };
      currentX += 1;
      rowMaxH = Math.max(rowMaxH, h);
      if (currentX >= PWA_DASHBOARD_COLS) {
        currentY += rowMaxH;
        currentX = 0;
        rowMaxH = 0;
      }
      return layout;
    }

    flushPartialRow();
    const layout: PwaDashboardLayoutItem = {
      i: cardId,
      x: 0,
      y: currentY,
      w: PWA_DASHBOARD_COLS,
      h,
      minW: PWA_DASHBOARD_COLS,
      minH,
    };
    currentY += h;
    return layout;
  });
}

export function getPwaMobileDashboardLayouts(cards: CardId[]) {
  const layout = generatePwaMobileDashboardLayout(cards);
  return {
    xl: layout,
    lg: layout,
    md: layout,
    sm: layout,
    xs: layout,
    xxs: layout,
  };
}

export const PWA_DASHBOARD_BREAKPOINT_COLS = {
  xl: PWA_DASHBOARD_COLS,
  lg: PWA_DASHBOARD_COLS,
  md: PWA_DASHBOARD_COLS,
  sm: PWA_DASHBOARD_COLS,
  xs: PWA_DASHBOARD_COLS,
  xxs: PWA_DASHBOARD_COLS,
} as const;
